import { useState, useEffect } from 'react';
import { supabase } from '@/shared/integrations/supabase/client';
import { useToast } from '@/shared/hooks/use-toast';
import { assertNotDemo, isDemoUser } from '@/shared/lib/userIdUtils';

export interface VRTour {
  id: string;
  property_id: string;
  link_type: string;
  link_url: string;
  link_name?: string;
  link_metadata?: {
    matterport_model_id?: string;
    tour_description?: string;
    thumbnail_url?: string;
    view_count?: number;
    [key: string]: any;
  };
  created_at: string;
  updated_at?: string;
}

export interface VRTourAsset {
  id: string;
  tour_id: string;
  asset_id: string;
  hotspot_position: any;
  hotspot_data: any;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** Synthetic tour id when the tour comes from properties.matterport_url (no property_links row). */
export const PROPERTY_MATTERPORT_TOUR_ID_PREFIX = 'property-matterport-';

export function useVRTours(propertyId: string) {
  const [tours, setTours] = useState<VRTour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchTours = async () => {
    try {
      setLoading(true);

      const linksRes = await supabase
        .from('property_links')
        .select('*')
        .eq('property_id', propertyId)
        .eq('link_type', 'matterport')
        .order('created_at', { ascending: false });

      if (linksRes.error) throw linksRes.error;
      const linkTours = (linksRes.data || []) as VRTour[];

      setTours(linkTours);
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error loading VR tours",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createTour = async (tourData: Partial<VRTour> & { matterport_model_id?: string; tour_name?: string; tour_description?: string; thumbnail_url?: string }) => {
    try {
      assertNotDemo("Creating VR tours");
      const { data, error } = await supabase
        .from('property_links')
        .insert([{
          property_id: propertyId,
          link_type: 'matterport',
          link_url: tourData.link_url || '',
          link_name: tourData.tour_name || tourData.link_name || 'Matterport Tour',
          link_metadata: {
            matterport_model_id: tourData.matterport_model_id,
            tour_description: tourData.tour_description,
            thumbnail_url: tourData.thumbnail_url,
            view_count: 0
          }
        }])
        .select()
        .single();

      if (error) throw error;

      setTours(prev => [data as VRTour, ...prev]);
      toast({
        title: "VR Tour Created",
        description: "Your VR tour has been successfully added.",
      });

      return data as VRTour;
    } catch (err: any) {
      toast({
        title: "Error creating VR tour",
        description: err.message,
        variant: "destructive",
      });
      throw err;
    }
  };

  const updateTour = async (tourId: string, updates: Partial<VRTour> & { matterport_model_id?: string; tour_name?: string; tour_description?: string; thumbnail_url?: string }) => {
    try {
      assertNotDemo("Updating VR tours");
      const updateData: any = {
        link_url: updates.link_url,
        link_name: updates.tour_name || updates.link_name,
        link_metadata: {}
      };

      if (updates.matterport_model_id || updates.tour_description || updates.thumbnail_url) {
        updateData.link_metadata = {
          matterport_model_id: updates.matterport_model_id,
          tour_description: updates.tour_description,
          thumbnail_url: updates.thumbnail_url
        };
      }

      const { data, error } = await supabase
        .from('property_links')
        .update(updateData)
        .eq('id', tourId)
        .select()
        .single();

      if (error) throw error;

      setTours(prev => prev.map(tour => 
        tour.id === tourId ? { ...tour, ...(data as VRTour) } : tour
      ));

      toast({
        title: "VR Tour Updated",
        description: "Your VR tour has been successfully updated.",
      });

      return data as VRTour;
    } catch (err: any) {
      toast({
        title: "Error updating VR tour",
        description: err.message,
        variant: "destructive",
      });
      throw err;
    }
  };

  const deleteTour = async (tourId: string) => {
    try {
      assertNotDemo("Deleting VR tours");
      const { error } = await supabase
        .from('property_links')
        .delete()
        .eq('id', tourId);

      if (error) throw error;

      setTours(prev => prev.filter(tour => tour.id !== tourId));
      toast({
        title: "VR Tour Deleted",
        description: "Your VR tour has been successfully removed.",
      });
    } catch (err: any) {
      toast({
        title: "Error deleting VR tour",
        description: err.message,
        variant: "destructive",
      });
      throw err;
    }
  };

  const incrementViewCount = async (tourId: string) => {
    // Synthetic tour from property.matterport_url has no property_links row; skip.
    if (tourId.startsWith(PROPERTY_MATTERPORT_TOUR_ID_PREFIX)) return;
    // Demo is read-only: skip the analytics write silently (non-user-initiated).
    if (isDemoUser()) return;
    try {
      // Get current tour data
      const { data: currentTour } = await supabase
        .from('property_links')
        .select('link_metadata')
        .eq('id', tourId)
        .single();

      if (currentTour && currentTour.link_metadata) {
        const metadata = currentTour.link_metadata as any;
        const currentViewCount = metadata.view_count || 0;
        await supabase
          .from('property_links')
          .update({ 
            link_metadata: {
              ...metadata,
              view_count: currentViewCount + 1
            }
          })
          .eq('id', tourId);
      }
    } catch (err) {
      console.error('Error incrementing view count:', err);
    }
  };

  useEffect(() => {
    if (propertyId) {
      fetchTours();
    }
  }, [propertyId]);

  return {
    tours,
    loading,
    error,
    createTour,
    updateTour,
    deleteTour,
    incrementViewCount,
    refetch: fetchTours,
  };
}